import { useState } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "../components/Wordmark";
import { ArrowLeft, HelpCircle, ChevronDown } from "lucide-react";

const FAQS = [
  {
    group: "Getting started",
    items: [
      { q: "What is Write My Story?", a: "It's a gentle way to write your life story. You answer warm questions by speaking or typing, and the app helps shape your memories into flowing, book-ready prose — organised into chapters over time." },
      { q: "Do I need to be a good writer?", a: "Not at all. Just talk or type naturally, as if you were telling a friend. We turn your words into readable prose, and you can edit everything to sound just like you." },
      { q: "Is there a free trial?", a: "Yes — 7 days free, with no card required. When the trial ends you can choose a plan to carry on. Everything you've written is saved and waiting for you." },
    ],
  },
  {
    group: "Signing in & getting back in",
    items: [
      { q: "How do I get back into my account next time?", a: "Go to the same web address you used when you signed up, tap “Log in”, and enter the email and password you chose. It's a good idea to write your email and password down somewhere safe the very first time — even a notebook by the computer — so they're always to hand." },
      { q: "How do I save the page so it's easy to find again?", a: "Add it to your favourites (also called bookmarks). On a computer, open the site and click the star in the address bar (or press Ctrl+D, and ⌘+D on a Mac). On a phone or tablet, tap your browser's share or menu button and choose “Add bookmark” or “Add to Favourites”. Next time, just open your favourites and you're one tap away." },
      { q: "Can I add Write My Story to my phone like an app?", a: "Yes — and it's the easiest way back in. On an iPhone or iPad, open the site in Safari, tap the share button (the square with an arrow), then “Add to Home Screen”. On an Android phone, open it in Chrome, tap the menu (three dots), then “Add to Home screen” or “Install app”. An icon then sits on your home screen, so you can open it in one tap with no web address to remember." },
      { q: "I've forgotten my password — what do I do?", a: "Don't worry, it happens to all of us. On the login screen tap “Forgot password?”, enter your email, and we'll email you a link to choose a new one. Your story is completely safe and waiting — a forgotten password never affects anything you've written." },
      { q: "Do I have to log in every time?", a: "Usually not. If you use the same device and browser, you'll normally stay signed in. And if you've added the app to your home screen (see above), it remembers you and opens straight to your story." },
    ],
  },
  {
    group: "Recording & writing",
    items: [
      { q: "Can I speak instead of type?", a: "Yes. Tap “Speak” and simply talk — we transcribe your recording and turn it into written prose. You can also type if you prefer, and switch between the two at any time." },
      { q: "What if I don't know where to start?", a: "Your home screen always shows a question for today. You can tap “Ask me something else” for a different one, or open “Help Me Remember” to browse a library of 150+ questions covering childhood, family, work, love and life lessons. There's no right place to begin — pick whichever question stirs a memory." },
      { q: "Can I edit what the app writes?", a: "Always. Every draft is yours to refine, rewrite or expand. The app helps with a first draft; the final words are entirely your choice." },
    ],
  },
  {
    group: "Your finished memoir",
    items: [
      { q: "How is my finished memoir presented?", a: "As a complete book. It includes your title and subtitle, a dedication, an introduction, your chapters in order, and acknowledgements. You can read the whole thing in the app using “Read my memoir” on the My Book page." },
      { q: "What formats can I download?", a: "You can download your memoir as a Word document (.docx) and as a print-ready PDF. The PDF is formatted as a proper 6×9-inch book — with a designed cover, title page, contents, page numbers and your chapters — ready to keep, share with family, or send straight to a printer." },
      { q: "Can I design a cover for my book?", a: "Yes. On the My Book page you can add your author name and choose from tasteful cover styles, or create bespoke AI cover artwork just by describing the scene or mood you'd like. Your finished cover appears at the front of the downloaded PDF." },
      { q: "Can I include photos?", a: "Yes — the photos you add to your memories now appear within the relevant chapters of your downloaded PDF book, right alongside the stories they belong to." },
    ],
  },
  {
    group: "Turning it into a physical book",
    items: [
      { q: "Can I get a printed, physical copy?", a: "Yes. Your book downloads as a print-ready PDF at the standard 6×9-inch book size — with your designed cover, page numbers and photos — so you can take it to any print-on-demand service or local printer to be bound as a paperback or hardback. A built-in “order a printed book” option is coming soon, so you'll be able to order a copy without leaving the app." },
      { q: "How much does printing cost?", a: "Printing is handled by the print service you choose, so the price depends on them — mainly the page count and whether you pick paperback or hardback. Creating and downloading your book is included in your subscription; you only pay the printer for the physical copies you order." },
      { q: "Can I make copies for family?", a: "Absolutely. Because you get a downloadable file, you can print as many copies as you like for children, grandchildren and friends — a lovely keepsake to pass down." },
    ],
  },
  {
    group: "Family & friends",
    items: [
      { q: "Can my family add their memories?", a: "Yes. Share your personal contribution link and loved ones can add their own memories about you — no account needed. Each contribution stays private until you review it, and you choose which ones to add to your story." },
    ],
  },
  {
    group: "Privacy & billing",
    items: [
      { q: "Who can see my story?", a: "Only you. Your memories are private to your account and are never shared publicly. See our Privacy Policy for full details." },
      { q: "Can I cancel any time?", a: "Yes. Your plan can be cancelled whenever you like, and you'll keep access until the end of your current paid period." },
      { q: "How do I delete my data?", a: "You're in control. From Settings you can delete your account and all of its content at any time." },
    ],
  },
];

function Item({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-line rounded-2xl bg-white overflow-hidden">
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-cream/50 transition-colors"
      >
        <span className="font-serif text-lg text-charcoal">{q}</span>
        <ChevronDown size={20} className={`text-sage shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-charcoal/85 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function FAQ({ embedded = false }) {
  const body = (
    <main className={embedded ? "max-w-3xl mx-auto" : "max-w-3xl mx-auto px-4 sm:px-6 py-12 relative z-10"}>
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold">
          <HelpCircle size={14} /> Help & answers
        </span>
        <h1 className="font-serif text-4xl text-charcoal mt-3">Frequently asked questions</h1>
        <p className="text-subtle mt-2 leading-relaxed">Everything you might want to know about writing your story, sharing it with family, and turning it into a book.</p>

        {FAQS.map((section, si) => (
          <section key={section.group} className="mt-10">
            <h2 className="font-serif text-2xl text-teal">{section.group}</h2>
            <div className="mt-4 space-y-3">
              {section.items.map((it, ii) => (
                <Item key={it.q} q={it.q} a={it.a} testid={`faq-item-${si}-${ii}`} />
              ))}
            </div>
          </section>
        ))}

        <div className="mt-12 bg-white rounded-2xl border border-line p-6 text-center">
          <p className="font-serif text-xl text-charcoal">Still have a question?</p>
          <p className="text-subtle mt-1.5">We're happy to help. Email <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a>.</p>
          <div className="mt-4 text-sm text-subtle">
            See also our <Link to="/privacy" className="text-teal underline">Privacy Policy</Link> and <Link to="/terms" className="text-teal underline">Terms of Service</Link>.
          </div>
        </div>
    </main>
  );

  if (embedded) return <div data-testid="faq-page" className="animate-fade-in">{body}</div>;

  return (
    <div className="min-h-screen grain bg-cream" data-testid="faq-page">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="faq-home-link">
            <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
            <Wordmark size="text-lg sm:text-xl" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>
      {body}
    </div>
  );
}
